import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Alert,
  Paper,
  Chip,
  Stack,
  CircularProgress,
  Divider,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Person as PersonIcon,
  Category as CategoryIcon,
  LocationOn as LocationIcon
} from '@mui/icons-material';
import authService from '../../services/auth/authService';
import { disciplinesApi } from '../../services/api';
import NetworkSectionSelector from './NetworkSectionSelector';

const SUB_DISCIPLINES = ['Pavement Defect', 'Traffic Safety'];

const AccessRequestReviewModal = ({ open, onClose, request, onRequestProcessed }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  // Editable fields for approval
  const [role, setRole] = useState('viewer');
  const [permissionScope, setPermissionScope] = useState('network_section');
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [assignableResources, setAssignableResources] = useState({ networks: [] });
  const [availableDisciplines, setAvailableDisciplines] = useState([]);
  const [selectedDisciplines, setSelectedDisciplines] = useState([]);
  const [selectedSubDisciplines, setSelectedSubDisciplines] = useState([]);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (open && request) {
      // Initialize from request data
      setRole(request.requested_role || request.role || 'viewer');
      setPermissionScope(request.requested_permission_scope || 'network_section');
      setSelectedPermissions(request.requested_locations || []);
      setSelectedDisciplines(request.requested_disciplines || []);
      setSelectedSubDisciplines(request.requested_sub_disciplines || []);
      setError(null);
      setShowRejectForm(false);
      setRejectReason('');
      setNotes('');
      fetchDecisionOptions();
    }
  }, [open, request]);

  const fetchDecisionOptions = async () => {
    try {
      const [resources, disciplineResponse] = await Promise.all([
        authService.getAvailableResources(),
        disciplinesApi.getDisciplines()
      ]);
      setAssignableResources(resources || { networks: [] });
      setAvailableDisciplines(disciplineResponse.data || []);
    } catch (err) {
      console.error('Failed to fetch review options:', err);
      setError('Failed to load available permissions');
    }
  };

  const toggleDiscipline = (discipline) => {
    const selected = selectedDisciplines.some(item => item.id === discipline.id);
    setSelectedDisciplines(selected
      ? selectedDisciplines.filter(item => item.id !== discipline.id)
      : [...selectedDisciplines, { id: discipline.id, name: discipline.name }]);
    if (selected && discipline.name === 'Road Pavement') setSelectedSubDisciplines([]);
  };

  const toggleSubDiscipline = (subDiscipline) => {
    setSelectedSubDisciplines(previous => previous.includes(subDiscipline)
      ? previous.filter(item => item !== subDiscipline)
      : [...previous, subDiscipline]);
  };

  const handleApprove = async () => {
    setLoading(true);
    setError(null);
    try {
      await authService.approveAccessRequest(request.id, {
        disciplines: selectedDisciplines,
        subDisciplines: selectedSubDisciplines,
        locations: selectedPermissions,
        role,
        permissionScope,
        notes: notes || undefined
      });
      onRequestProcessed?.();
      onClose();
    } catch (err) {
      setError(err.error || 'Failed to approve request');
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      setError('Please provide a reason for rejection');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await authService.rejectAccessRequest(request.id, rejectReason);
      onRequestProcessed?.();
      onClose();
    } catch (err) {
      setError(err.error || 'Failed to reject request');
    } finally {
      setLoading(false);
    }
  };

  if (!request) return null;

  const requestedLocations = request.requested_locations || [];
  const isChangeRequest = request.request_type === 'access_change';
  const roadPavementSelected = selectedDisciplines.some(item => item.name === 'Road Pavement');
  const disciplinesValid = selectedDisciplines.length > 0 && (!roadPavementSelected || selectedSubDisciplines.length > 0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonIcon color="primary" />
            <Typography variant="h6">Review {isChangeRequest ? 'Access Change' : 'Access'} Request</Typography>
          </Box>
          <Button onClick={onClose} size="small" color="inherit">
            <CloseIcon />
          </Button>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* User Info */}
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>User Information</Typography>
          <Stack spacing={0.5}>
            <Typography><b>Name:</b> {request.full_name || request.username}</Typography>
            <Typography><b>Email:</b> {request.email}</Typography>
            <Typography><b>Username:</b> {request.username}</Typography>
            <Typography><b>Requested:</b> {new Date(request.created_at).toLocaleString()}</Typography>
          </Stack>
        </Paper>

        {isChangeRequest && (
          <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'background.default' }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>Current Access (remains active until approval)</Typography>
            <Stack spacing={1}>
              <Typography variant="body2"><b>Role:</b> {request.current_user_role}</Typography>
              <Typography variant="body2"><b>Scope:</b> {request.current_permission_scope === 'organization' ? 'Organization-wide' : 'Network/Section'}</Typography>
              <Box>
                <Typography variant="caption" color="text.secondary">Disciplines</Typography>
                <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                  {(request.current_disciplines || []).map(item => <Chip key={item.id} label={item.name} size="small" />)}
                </Stack>
              </Box>
              {(request.current_sub_disciplines || []).length > 0 && (
                <Box>
                  <Typography variant="caption" color="text.secondary">Sub-disciplines</Typography>
                  <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                    {request.current_sub_disciplines.map(item => <Chip key={item} label={item} size="small" variant="outlined" />)}
                  </Stack>
                </Box>
              )}
              <Box>
                <Typography variant="caption" color="text.secondary">Locations</Typography>
                <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                  {request.current_permission_scope === 'organization'
                    ? <Chip label="All organization locations" size="small" />
                    : (request.current_locations || []).map(item => <Chip key={`${item.type}-${item.id}`} label={item.name} size="small" />)}
                </Stack>
              </Box>
              {request.request_reason && <Alert severity="info"><b>Reason:</b> {request.request_reason}</Alert>}
            </Stack>
          </Paper>
        )}

        {/* Requested disciplines remain editable until approval. */}
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <CategoryIcon fontSize="small" color="primary" />
            <Typography variant="subtitle2">Assign Disciplines</Typography>
          </Box>
          <Typography variant="caption" color="text.secondary">
            The user's requested disciplines are preselected. Adjust them before approval if needed.
          </Typography>
          <Stack spacing={0.5} sx={{ mt: 1 }}>
            {availableDisciplines.map(discipline => (
              <Box key={discipline.id}>
                <FormControlLabel
                  control={<Checkbox size="small" checked={selectedDisciplines.some(item => item.id === discipline.id)} onChange={() => toggleDiscipline(discipline)} />}
                  label={<Typography variant="body2">{discipline.name}</Typography>}
                />
                {discipline.name === 'Road Pavement' && roadPavementSelected && (
                  <Stack direction={{ xs: 'column', sm: 'row' }} sx={{ pl: 4 }}>
                    {SUB_DISCIPLINES.map(subDiscipline => (
                      <FormControlLabel
                        key={subDiscipline}
                        control={<Checkbox size="small" checked={selectedSubDisciplines.includes(subDiscipline)} onChange={() => toggleSubDiscipline(subDiscipline)} />}
                        label={<Typography variant="body2">{subDiscipline}</Typography>}
                      />
                    ))}
                  </Stack>
                )}
              </Box>
            ))}
          </Stack>
          {!disciplinesValid && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              Select at least one discipline and, for Road Pavement, at least one sub-discipline.
            </Alert>
          )}
        </Paper>

        {/* Requested Locations (read-only display) */}
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <LocationIcon fontSize="small" color="warning" />
            <Typography variant="subtitle2">Requested Locations (User's Preference)</Typography>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {requestedLocations.map((loc, i) => (
              <Chip
                key={i}
                label={`${loc.type === 'network' ? 'Network' : 'Section'}: ${loc.name}`}
                size="small"
                color={loc.type === 'network' ? 'info' : 'default'}
                variant="outlined"
              />
            ))}
            {requestedLocations.length === 0 && (
              <Typography variant="body2" color="text.secondary">No locations requested</Typography>
            )}
          </Box>
        </Paper>

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Manager Decision
        </Typography>

        {/* Role & Scope Selection */}
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Role</InputLabel>
            <Select value={role} onChange={(e) => setRole(e.target.value)} label="Role">
              <MenuItem value="viewer">Viewer</MenuItem>
              <MenuItem value="inspector">Inspector</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel>Permission Scope</InputLabel>
            <Select
              value={permissionScope}
              onChange={(e) => setPermissionScope(e.target.value)}
              label="Permission Scope"
            >
              <MenuItem value="organization">Organization-wide</MenuItem>
              <MenuItem value="network_section">Network/Section</MenuItem>
            </Select>
          </FormControl>
        </Stack>

        {/* Location Assignment (editable) */}
        {permissionScope === 'network_section' && (
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Assign Locations (you can modify the user's request)
            </Typography>
            <NetworkSectionSelector
              assignableResources={assignableResources}
              selectedPermissions={selectedPermissions}
              onChange={setSelectedPermissions}
              error={null}
            />
          </Paper>
        )}

        {permissionScope === 'organization' && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Organization-wide access grants access to all networks and sections. No specific location assignment needed.
          </Alert>
        )}

        {/* Notes */}
        <TextField
          fullWidth
          size="small"
          label="Notes (optional)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          multiline
          rows={2}
          sx={{ mb: 2 }}
        />

        {/* Reject Form */}
        {showRejectForm && (
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'error.50' }}>
            <Typography variant="subtitle2" color="error" gutterBottom>Rejection Reason (required)</Typography>
            <TextField
              fullWidth
              size="small"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              multiline
              rows={2}
              placeholder="Explain why the request is being rejected..."
            />
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <Button
                variant="contained"
                color="error"
                size="small"
                onClick={handleReject}
                disabled={loading || !rejectReason.trim()}
                startIcon={loading ? <CircularProgress size={14} /> : <RejectIcon />}
              >
                Confirm Reject
              </Button>
              <Button size="small" onClick={() => { setShowRejectForm(false); setRejectReason(''); }}>
                Cancel
              </Button>
            </Stack>
          </Paper>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2 }}>
        {!showRejectForm && (
          <Button
            color="error"
            onClick={() => setShowRejectForm(true)}
            disabled={loading}
          >
            Reject
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button
          variant="contained"
          color="success"
          onClick={handleApprove}
          disabled={loading || !disciplinesValid || (permissionScope === 'network_section' && selectedPermissions.length === 0)}
          startIcon={loading ? <CircularProgress size={16} /> : <ApproveIcon />}
        >
          Approve
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AccessRequestReviewModal;
